import { cn } from "@/utils/cn";
import Placeholder from "@tiptap/extension-placeholder";
import { Editor, EditorContent, useEditor, UseEditorOptions } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";

export type EditableDivKeyDownHandler = ({ text, event, clearContent }: { text: string; event: KeyboardEvent; clearContent: () => void }) => void;
export type EditableDivChangeHandler = (text: string, editor?: Editor) => void;

export const editorIsReady = (editor?: Editor | null) => {
  try {
    return !!editor && !!editor.view && !!editor.view.dom;
  } catch {
    return false;
  }
};

export interface EditableDivProps {
  id?: string;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  className?: string;
  editorClassName?: string;
  height?: number;
  maxHeight?: number;
  disabled?: boolean;
  focusOnMount?: boolean;
  placeholder?: string;
  handleEnterKey?: EditableDivKeyDownHandler;
  handleChange?: EditableDivChangeHandler;
}

export interface EditableDivHandle {
  get node(): HTMLDivElement | null;
  get text(): string;
  get editor(): Editor | null;
  set: (text: string) => void;
  setText: (plainText: string) => void;
  clear: () => void;
  focus: () => void;
}

const useEditorConfig = ({
  id,
  ariaLabel,
  ariaLabelledBy,
  editorClassName,
  placeholder,
  enterKeyRef,
  changeRef,
}: {
  id?: string;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  editorClassName?: string;
  placeholder?: string;
  enterKeyRef: React.RefObject<EditableDivKeyDownHandler | undefined>;
  changeRef: React.RefObject<EditableDivChangeHandler | undefined>;
}): UseEditorOptions =>
  useMemo(() => {
    return {
      extensions: [
        StarterKit.configure({
          bold: false,
          italic: false,
          strike: false,
          code: false,
          heading: false,
          bulletList: false,
          orderedList: false,
          listItem: false,
          blockquote: false,
          codeBlock: false,
          horizontalRule: false,
        }),
        Placeholder.configure({
          placeholder: placeholder ?? "Begin typing here...",
          showOnlyWhenEditable: false,
        }),
      ],
      editorProps: {
        attributes: {
          class: cn("flex-1 px-4 py-3 bg-transparent outline-none text-base min-h-[1.5rem]", editorClassName),
          role: "textbox",
          "aria-multiline": "true",
          ...(id ? { id } : {}),
          ...(ariaLabel ? { "aria-label": ariaLabel } : {}),
          ...(ariaLabelledBy ? { "aria-labelledby": ariaLabelledBy } : {}),
        },
        handleKeyDown: (view, event) => {
          if (event.key === "Enter" && !event.shiftKey) {
            enterKeyRef.current?.({
              text: view.state.doc.textContent,
              event,
              clearContent: () => view.dispatch(view.state.tr.delete(0, view.state.doc.content.size)),
            });
            return true;
          }
          return false;
        },
        handlePaste: (view, event) => {
          const text = event.clipboardData?.getData("text/plain");
          if (text) {
            view.dispatch(view.state.tr.insertText(text));
            return true;
          }
          return false;
        },
      },
      content: "",
      onUpdate: ({ editor }) => {
        const text = editor.getText();
        changeRef.current?.(text, editor);
      },
    };
  }, [id, ariaLabel, ariaLabelledBy, editorClassName, placeholder, enterKeyRef, changeRef]);

const EditableDiv = forwardRef<EditableDivHandle, EditableDivProps>((props, ref) => {
  const { id, ariaLabel, ariaLabelledBy, className, editorClassName, disabled, focusOnMount, height, maxHeight = 270, placeholder, handleEnterKey, handleChange } = props;

  const [didFocusOnMount, setDidFocusOnMount] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);

  const enterKeyRef = useRef(handleEnterKey);
  enterKeyRef.current = handleEnterKey;
  const changeRef = useRef(handleChange);
  changeRef.current = handleChange;

  const config = useEditorConfig({ id, ariaLabel, ariaLabelledBy, editorClassName, placeholder, enterKeyRef, changeRef });
  const editor = useEditor(config, [placeholder]);

  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [editor, disabled]);

  // `editor` is null during server rendering (TipTap defers creation to the
  // client), so every access here is optional. "/" is server-rendered.
  useEffect(() => {
    if (editor?.isInitialized && focusOnMount && !didFocusOnMount) {
      editor.commands.focus();
      setDidFocusOnMount(true);
    }
  }, [didFocusOnMount, editor, editor?.isInitialized, focusOnMount]);

  useEffect(() => {
    if (!editorIsReady(editor)) return;
    const dom = editor.view.dom as HTMLElement;
    dom.style.height = height ? `${height}px` : "";
    dom.style.maxHeight = maxHeight ? `${maxHeight}px` : "";
    dom.style.overflowY = (height ?? 0) > maxHeight ? "scroll" : "";
  }, [editor, height, maxHeight]);

  useImperativeHandle(
    ref,
    () => ({
      get node() {
        return editorRef.current;
      },
      get text() {
        return editor?.getText() ?? "";
      },
      get editor() {
        return editor ?? null;
      },
      set: (text: string) => {
        editor?.commands.setContent(text, { emitUpdate: true });
      },
      setText: (plainText: string) => {
        if (!editorIsReady(editor)) return;
        const lines = plainText.split("\n");
        const content = lines.map((line) => ({
          type: "paragraph" as const,
          ...(line ? { content: [{ type: "text" as const, text: line }] } : {}),
        }));
        editor.commands.setContent({ type: "doc", content }, { emitUpdate: true });
      },
      clear: () => {
        editor?.commands.clearContent(true);
      },
      focus: () => {
        if (editor?.isInitialized) {
          editor?.commands.focus();
        }
      },
    }),
    [editor],
  );

  return <EditorContent ref={editorRef} editor={editor} className={className} />;
});

EditableDiv.displayName = "EditableDiv";

export { EditableDiv };
